/**
 * CouponFormPage - Create or edit a coupon/discount code
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getCoupon,
  createCoupon,
  updateCoupon,
  deleteCoupon,
} from '../../../api/admin';
import type { CouponUsage } from '../../../api/admin';
import { formatPrice } from '../../../api/events';

export default function CouponFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form state
  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [appliesTo, setAppliesTo] = useState<'total' | 'sessions_only' | 'addons_only'>('total');
  const [maxUses, setMaxUses] = useState('');
  const [unlimitedUses, setUnlimitedUses] = useState(true);
  const [singleUsePerStudio, setSingleUsePerStudio] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [noExpiry, setNoExpiry] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [internalNotes, setInternalNotes] = useState('');

  // Usage history (edit mode only)
  const [usages, setUsages] = useState<CouponUsage[]>([]);

  useEffect(() => {
    if (isEditing && id) {
      loadCoupon(id);
    }
  }, [id, isEditing]);

  const loadCoupon = async (couponId: string) => {
    try {
      setIsLoading(true);
      const coupon = await getCoupon(couponId);
      setCode(coupon.code);
      setDiscountType(coupon.discount_type);

      // Convert from stored value to display value
      if (coupon.discount_type === 'fixed') {
        // Stored as cents, display as dollars
        setDiscountValue(String(coupon.discount_value / 100));
      } else {
        // Stored as basis points, display as whole percentage
        setDiscountValue(String(coupon.discount_value / 100));
      }

      setAppliesTo(coupon.applies_to);

      if (coupon.max_uses != null) {
        setMaxUses(String(coupon.max_uses));
        setUnlimitedUses(false);
      } else {
        setMaxUses('');
        setUnlimitedUses(true);
      }

      setSingleUsePerStudio(coupon.single_use_per_studio);

      if (coupon.expiry_date) {
        setExpiryDate(coupon.expiry_date.split('T')[0] || coupon.expiry_date);
        setNoExpiry(false);
      } else {
        setExpiryDate('');
        setNoExpiry(true);
      }

      setIsActive(coupon.is_active);
      setInternalNotes(coupon.internal_notes || '');
      setUsages(coupon.usages || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load coupon');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!code.trim()) {
      setError('Please enter a coupon code');
      return;
    }

    const numericValue = parseFloat(discountValue);
    if (isNaN(numericValue) || numericValue <= 0) {
      setError('Please enter a valid discount value');
      return;
    }

    // Convert display value to stored value
    let storedValue: number;
    if (discountType === 'fixed') {
      // User enters dollars, store as cents
      storedValue = Math.round(numericValue * 100);
    } else {
      // User enters percentage (e.g. 20), store as basis points (e.g. 2000)
      storedValue = Math.round(numericValue * 100);
    }

    try {
      setIsSaving(true);

      if (isEditing && id) {
        await updateCoupon(id, {
          discount_type: discountType,
          discount_value: storedValue,
          applies_to: appliesTo,
          max_uses: unlimitedUses ? null : parseInt(maxUses, 10),
          single_use_per_studio: singleUsePerStudio,
          expiry_date: noExpiry ? null : expiryDate,
          is_active: isActive,
          internal_notes: internalNotes.trim() || null,
        });
      } else {
        await createCoupon({
          code: code.trim().toUpperCase(),
          discount_type: discountType,
          discount_value: storedValue,
          applies_to: appliesTo,
          max_uses: unlimitedUses ? null : parseInt(maxUses, 10),
          single_use_per_studio: singleUsePerStudio,
          expiry_date: noExpiry ? null : expiryDate,
          is_active: isActive,
          internal_notes: internalNotes.trim() || null,
        });
      }

      navigate('/admin/coupons');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save coupon');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      setIsDeleting(true);
      setError(null);
      setShowDeleteConfirm(false);
      await deleteCoupon(id);
      navigate('/admin/coupons');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete coupon');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div>
        <div className="h-10 bg-warm-200 rounded w-1/4 mb-8 animate-pulse" />
        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="space-y-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 bg-warm-100 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Link
          to="/admin/coupons"
          className="p-2 text-warm-500 hover:text-warm-700 hover:bg-warm-100 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">
          {isEditing ? 'Edit Coupon' : 'New Coupon'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {error && (
              <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
                {error}
              </div>
            )}

            {/* Coupon Code */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-warm-900 mb-4">Coupon Code</h3>

              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Code *
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  disabled={isEditing}
                  className={`w-full px-4 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono ${
                    isEditing ? 'bg-warm-50 text-warm-500 cursor-not-allowed' : ''
                  }`}
                  placeholder="e.g., SUMMER20"
                  required
                />
                {isEditing && (
                  <p className="text-xs text-warm-500 mt-1">Code cannot be changed after creation</p>
                )}
              </div>
            </div>

            {/* Discount Settings */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-warm-900 mb-4">Discount Settings</h3>

              <div className="space-y-4">
                {/* Discount Type */}
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-2">
                    Discount Type
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="discount_type"
                        value="fixed"
                        checked={discountType === 'fixed'}
                        onChange={() => setDiscountType('fixed')}
                        className="h-4 w-4 border-warm-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-warm-700">Fixed Amount ($)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="discount_type"
                        value="percentage"
                        checked={discountType === 'percentage'}
                        onChange={() => setDiscountType('percentage')}
                        className="h-4 w-4 border-warm-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-warm-700">Percentage (%)</span>
                    </label>
                  </div>
                </div>

                {/* Discount Value */}
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Discount Value *
                  </label>
                  <div className="relative">
                    {discountType === 'fixed' && (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-500">$</span>
                    )}
                    <input
                      type="number"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                      min="0"
                      step={discountType === 'fixed' ? '0.01' : '1'}
                      className={`w-full py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        discountType === 'fixed' ? 'pl-8 pr-4' : 'pl-4 pr-8'
                      }`}
                      placeholder={discountType === 'fixed' ? '50.00' : '20'}
                      required
                    />
                    {discountType === 'percentage' && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-warm-500">%</span>
                    )}
                  </div>
                </div>

                {/* Applies To */}
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Applies To
                  </label>
                  <select
                    value={appliesTo}
                    onChange={(e) => setAppliesTo(e.target.value as 'total' | 'sessions_only' | 'addons_only')}
                    className="w-full px-4 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="total">Entire Order</option>
                    <option value="sessions_only">Sessions Only</option>
                    <option value="addons_only">Add-ons Only</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Usage Limits */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-warm-900 mb-4">Usage Limits</h3>

              <div className="space-y-4">
                {/* Max Uses */}
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Maximum Uses
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="number"
                      value={maxUses}
                      onChange={(e) => setMaxUses(e.target.value)}
                      min="1"
                      disabled={unlimitedUses}
                      className={`flex-1 px-4 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        unlimitedUses ? 'bg-warm-50 text-warm-400 cursor-not-allowed' : ''
                      }`}
                      placeholder="e.g., 100"
                    />
                    <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={unlimitedUses}
                        onChange={(e) => {
                          setUnlimitedUses(e.target.checked);
                          if (e.target.checked) setMaxUses('');
                        }}
                        className="h-4 w-4 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-warm-700">Unlimited</span>
                    </label>
                  </div>
                </div>

                {/* Single Use Per Studio */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="singleUsePerStudio"
                    checked={singleUsePerStudio}
                    onChange={(e) => setSingleUsePerStudio(e.target.checked)}
                    className="h-4 w-4 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="singleUsePerStudio" className="text-sm font-medium text-warm-700">
                    Limit to one use per studio
                  </label>
                </div>

                {/* Expiry Date */}
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Expiry Date
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="date"
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                      disabled={noExpiry}
                      className={`flex-1 px-4 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        noExpiry ? 'bg-warm-50 text-warm-400 cursor-not-allowed' : ''
                      }`}
                    />
                    <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={noExpiry}
                        onChange={(e) => {
                          setNoExpiry(e.target.checked);
                          if (e.target.checked) setExpiryDate('');
                        }}
                        className="h-4 w-4 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-warm-700">No expiry</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Internal Notes */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-warm-900 mb-4">Internal Notes</h3>

              <textarea
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                placeholder="Admin-only notes about this coupon (e.g., reason for creating, associated promotion)"
              />
            </div>

            {/* Usage History (edit mode) */}
            {isEditing && usages.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-warm-900 mb-4">
                  Usage History ({usages.length})
                </h3>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-warm-50 border-b border-warm-100">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-warm-500 uppercase tracking-wider">Studio</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-warm-500 uppercase tracking-wider">Booking</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-warm-500 uppercase tracking-wider">Discount</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-warm-500 uppercase tracking-wider">Date</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-warm-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-warm-200">
                      {usages.map((usage) => (
                        <tr key={usage.id} className="hover:bg-warm-50">
                          <td className="px-4 py-3 text-sm text-warm-900">
                            {usage.studio_name || usage.studio_id}
                          </td>
                          <td className="px-4 py-3 text-sm text-warm-600 font-mono">
                            {usage.booking_number || usage.booking_id.slice(0, 8)}
                          </td>
                          <td className="px-4 py-3 text-sm text-warm-900 font-medium">
                            {formatPrice(usage.discount_cents)}
                          </td>
                          <td className="px-4 py-3 text-sm text-warm-600">
                            {formatDate(usage.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            {usage.is_cancelled ? (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-warm-100 text-warm-600">
                                Cancelled
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-success-50 text-success-700">
                                Applied
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="order-first lg:order-none space-y-6">
            {/* Actions */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-warm-900 mb-4">Actions</h3>

              <div className="space-y-3">
                {/* Active toggle */}
                <div className="flex items-center justify-between py-2">
                  <label htmlFor="isActive" className="text-sm font-medium text-warm-700">
                    Active
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsActive(!isActive)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                      isActive ? 'bg-primary-600' : 'bg-warm-200'
                    }`}
                    role="switch"
                    aria-checked={isActive}
                    id="isActive"
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        isActive ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={isSaving || isDeleting}
                  className="w-full btn-primary disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Coupon'}
                </button>

                <Link
                  to="/admin/coupons"
                  className="block w-full text-center px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
                >
                  Cancel
                </Link>

                {isEditing && (
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isSaving || isDeleting}
                    className="w-full px-4 py-2 text-danger-600 border border-danger-200 rounded-lg hover:bg-danger-50 disabled:opacity-50 transition-colors font-medium"
                  >
                    {isDeleting ? 'Deleting...' : 'Delete Coupon'}
                  </button>
                )}
              </div>
            </div>

            {/* Info note */}
            {isEditing && (
              <div className="bg-warm-50 rounded-xl p-6">
                <h3 className="text-sm font-semibold text-warm-700 mb-2">Note</h3>
                <p className="text-sm text-warm-600">
                  The coupon code cannot be changed after creation. You can update the discount settings, usage limits, and status.
                </p>
              </div>
            )}
          </div>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-warm-900 mb-2">Delete Coupon?</h3>
            <p className="text-warm-600 mb-1">
              This will permanently delete the coupon <strong className="font-mono">{code}</strong>.
            </p>
            {usages.length > 0 && (
              <p className="text-sm text-warning-700 bg-warning-50 border border-warning-200 rounded-lg px-3 py-2 mt-2 mb-4">
                This coupon has been used {usages.length} time{usages.length !== 1 ? 's' : ''}. Deleting it will not reverse any applied discounts.
              </p>
            )}
            <p className="text-sm text-warm-500 mb-6">
              This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 transition-colors font-medium"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
